import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CourseListComponent } from '../../components/course-list/course-list.component';
import { ProgressCardComponent } from '../../components/progress-card/progress-card.component';
import { TranslatePipe } from '@ngx-translate/core';
import { CourseService } from '../../services/course.service';
import { CourseListHeaderComponent } from '../../components/course-list-header/course-list-header.component';
import { FilterChip } from '../../../../shared/interfaces/filter-chips.interface';
import { EmptyCourseListComponent } from '../../components/empty-course-list/empty-course-list.component';
import { filter } from 'rxjs/operators';

@Component({
  standalone: true,
  selector: 'app-my-studies',
  imports: [
    CommonModule,
    CourseListComponent,
    ProgressCardComponent,
    TranslatePipe,
    CourseListHeaderComponent,
    EmptyCourseListComponent,
  ],
  templateUrl: './my-studies.component.html',
  styleUrl: './my-studies.component.scss',
})
export class MyStudiesComponent {
  #courseService = inject(CourseService);
  #router = inject(Router);

  filterChips: FilterChip[] = [
    { label: 'app.components.chips.all', isActive: true },
    { label: 'app.components.chips.completed', isActive: false },
  ];

  constructor() {
    this.#watchMyStudiesPageEnabled();
  }

  #selectedFilter = signal<'all' | 'completed'>('all');

  courses = computed(() => {
    const data = this.#courseService.studyingCourses();
    const coursesList = data?.courses ?? [];
    const filter = this.#selectedFilter();

    if (filter === 'completed') {
      return coursesList.filter(
        (course) =>
          (course as { courseCompleted?: boolean }).courseCompleted === true,
      );
    }

    return coursesList;
  });

  completionRate = computed(() => this.#courseService.studyingCompletionRate());

  onChipSelected(selected: FilterChip): void {
    this.filterChips = this.filterChips.map((chip) => ({
      ...chip,
      isActive: chip.label === selected.label,
    }));

    // Update filter state based on which chip was selected
    if (selected.label === 'app.components.chips.completed') {
      this.#selectedFilter.set('completed');
    } else {
      this.#selectedFilter.set('all');
    }
  }

  #watchMyStudiesPageEnabled(): void {
    this.#courseService.isMyStudiesPageEnabled.set(true);

    this.#router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        const isOnMyStudiesPage = this.#router.url.includes(
          '/courses/my-studies',
        );
        this.#courseService.isMyStudiesPageEnabled.set(isOnMyStudiesPage);
      });
  }
}
